import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Sparkle, FilePlus, Flask } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { hasFeature } from "../../lib/features";
import { useCoshhMatches, CoshhMatchList } from "./library/CoshhProductSearch";

/** Pick a document type → subject / facts / brief → AI draft (or a blank document). */
export function HseNewDocumentDialog({ open, onClose, groups = [], types = [], presetType = "", presetSubject = "" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [typeKey, setTypeKey] = useState(presetType);
  const [subject, setSubject] = useState(presetSubject);
  const [meta, setMeta] = useState({});
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState("");
  const t = types.find((x) => x.key === (typeKey || presetType));
  const matches = useCoshhMatches(subject, open && t?.key === "coshh_assessment" && hasFeature(user, "hs_library"));
  const reset = () => { setTypeKey(""); setSubject(""); setMeta({}); setBrief(""); };
  const submit = async (ai) => {
    if (!t) return;
    if (t.subject_label && !subject.trim()) { toast.error(`${t.subject_label} is required`); return; }
    setBusy(ai ? "ai" : "blank");
    try {
      const { data } = await API.post(ai ? "/hse/documents/ai-draft" : "/hse/documents", { type: t.key, subject, meta, brief });
      toast.success(ai ? "Drafting with AI — this takes a minute or two" : "Blank document created");
      reset(); onClose();
      nav(`/health-safety/hse/${data.id}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't create the document"); } finally { setBusy(""); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hse-new-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-primary" /> New HSE document</DialogTitle></DialogHeader>
        {!t ? (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.key}>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">{g.label}</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {types.filter((x) => x.group === g.key).map((x) => (
                    <button key={x.key} type="button" onClick={() => setTypeKey(x.key)} className="text-left rounded-lg border border-border p-3 hover:border-primary/60 hover:bg-muted/40 transition-colors" data-testid={`hse-new-type-${x.key}`}>
                      <p className="text-sm font-semibold flex items-center gap-2">{x.label}{x.essential && <span className="text-[10px] rounded-sm bg-primary/10 text-primary px-1 py-px font-medium">Essential</span>}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{x.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
              <div><p className="text-sm font-semibold">{t.label}</p><p className="text-[11px] text-muted-foreground">{t.description}</p></div>
              {!presetType && <Button size="sm" variant="ghost" onClick={() => setTypeKey("")} data-testid="hse-new-change-type">Change type</Button>}
            </div>
            {t.subject_label && (
              <div>
                <Label>{t.subject_label} *</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t.subject_examples?.[0] ? `e.g. ${t.subject_examples[0]}` : ""} data-testid="hse-new-subject" />
                {t.subject_examples?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">{t.subject_examples.map((ex) => <button key={ex} type="button" onClick={() => setSubject(ex)} className="text-[11px] rounded-full border border-border px-2 py-0.5 hover:border-primary/60" data-testid={`hse-new-example-${ex.replace(/\W+/g, "_")}`}>{ex}</button>)}</div>
                )}
                {t.key === "coshh_assessment" && matches.items.length > 0 && (
                  <div className="mt-2 space-y-1.5" data-testid="hse-new-coshh-matches">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1"><Flask size={13} weight="fill" /> Already in the Template library — {matches.items.length} ready-made {matches.items.length === 1 ? "assessment matches" : "assessments match"} “{matches.term}”. Using one skips the AI draft.</p>
                    <CoshhMatchList items={matches.items} canEdit onUsed={() => { reset(); onClose(); }} />
                  </div>
                )}
              </div>
            )}
            {(t.meta || []).length > 0 && (
              <div className="grid sm:grid-cols-2 gap-2">
                {t.meta.map((m) => <div key={m.key}><Label className="text-xs">{m.label}</Label><Input value={meta[m.key] || ""} onChange={(e) => setMeta((x) => ({ ...x, [m.key]: e.target.value }))} data-testid={`hse-new-meta-${m.key}`} /></div>)}
              </div>
            )}
            <div>
              <Label>Brief for the AI <span className="text-muted-foreground font-normal">(optional — anything specific to include)</span></Label>
              <Textarea rows={4} value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="e.g. Engineers work alone in occupied offices; we hold CHAS; include our 'stop work' authority." data-testid="hse-new-brief" />
            </div>
            <p className="text-[11px] text-muted-foreground">The AI writes every section from your company HSE profile, the subject and this brief. Nothing goes live until you've reviewed and approved it. Reviews default to every {t.review_months} months.</p>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          {t && <Button variant="outline" onClick={() => submit(false)} disabled={!!busy} data-testid="hse-new-blank"><FilePlus size={14} className="mr-1" /> Start blank</Button>}
          {t && <Button onClick={() => submit(true)} disabled={!!busy} data-testid="hse-new-ai"><Sparkle size={14} weight="fill" className="mr-1" /> {busy === "ai" ? "Starting…" : "Draft with AI"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
